import { Button, Select, Spin } from 'antd';
import React from 'react';
import { Acvt } from '../../../components/advanced-ct-visualisation-tool';
import { useContestStore } from '../../../contexts/mobx';
import {
    Contest,
    ContestPreviewStart,
    ContestPreviewToken,
    ContestTheme,
} from '../../../stores/contest';
import style from './style.module.scss';

type PreviewLanguage = 'fr' | 'en';

/** What one language of a policy draft says, as the web application reads it. */
export type PreviewPolicyContent =
    | { type: 'html'; body: string }
    | { type: 'pdf'; documentUrl: string }
    | { type: 'external_url'; externalUrl: string };

/**
 * A policy of the Policies tab as edited, which the preview shows before any
 * save. `PreviewPolicyDraft` in `apps/contests` reads the same shape. A
 * policy not yet saved has a negative id, and `provinces` names where it
 * applies, the contest's own when the policy names none.
 */
export interface PreviewPolicyDraft {
    id: number;
    provinces: string[];
    effectiveAt: string | null;
    translations: Partial<
        Record<
            PreviewLanguage,
            {
                title: string;
                urlSegment: string;
                acceptanceLabel: string | null;
                content: PreviewPolicyContent | null;
            }
        >
    >;
}

/**
 * The sign-up's address in the web application, after the contest's. It
 * follows the language, as `CONTEST_SIGNUP_ROUTES` in `apps/contests` does.
 */
const SIGNUP_SEGMENTS: Record<PreviewLanguage, string> = {
    fr: 'identification',
    en: 'signin',
};

/** The policy route's fixed segment, as `CONTEST_POLICY_ROUTES` in `apps/contests` names it. */
const POLICY_SEGMENTS: Record<PreviewLanguage, string> = {
    fr: 'politiques',
    en: 'policies',
};

/**
 * The frame says it listens, and is answered with the theme and the policies
 * being edited. `usePreviewDraft` in `apps/contests` names the same messages.
 */
const PREVIEW_READY_MESSAGE = 'contest-preview:ready';
const PREVIEW_THEME_MESSAGE = 'contest-preview:theme';
const PREVIEW_POLICIES_MESSAGE = 'contest-preview:policies';

/**
 * The brand the web application reads from `?brand=`, as a native shell
 * passes it: it decides which application's policies the drawer lists.
 */
function brandOf(province: string): string {
    return province === 'QC' ? 'ct' : 'ck';
}

/**
 * The token goes in the fragment, which the browser never sends: it stays out
 * of the web application's access logs.
 */
function previewUrl(address: string, token: string, province: string): string {
    const url = new URL(address);
    url.searchParams.set('brand', brandOf(province));
    url.hash = new URLSearchParams({ preview: token }).toString();
    return url.toString();
}

/**
 * The contest as the web application shows it, published or not, in a
 * phone: to a participant already signed up, or to a visitor at the sign-up.
 * Nothing done in it is kept. The frame reloads with the contest, so a save
 * shows at once. `theme`, the Theme tab as edited, shows before any save;
 * null leaves the frame with the theme it was served. `policies`, the
 * Policies tab as edited, stand for the served ones the same way. Each
 * `policyRequest` opens the frame on the page `policyPage` names then, in
 * French unless the policy has an address in English only, until the start
 * or the province changes.
 *
 * A preview lasts an hour. It is not renewed behind the person's back, which
 * would reload the frame and lose what they were doing: once it expires, the
 * phone says so and opens another on request.
 */
export const ContestPreview = ({
    contest,
    theme,
    policies,
    provinces,
    policyPage = null,
    policyRequest = 0,
}: {
    contest: Contest;
    /** The provinces to sign the participant up in, the contest's as edited. */
    provinces: string[];
    theme: ContestTheme | null;
    policies: PreviewPolicyDraft[];
    /** The policy's address in each language. */
    policyPage?: Partial<Record<PreviewLanguage, string>> | null;
    /** Grows each time a policy's page is asked for. */
    policyRequest?: number;
}) => {
    const store = useContestStore();
    const frame = React.useRef<HTMLIFrameElement | null>(null);

    const [start, setStart] = React.useState<ContestPreviewStart>(
        'participant'
    );
    const [province, setProvince] = React.useState<string>('QC');
    // The policy page asked for last, by its address then: typing in the
    // address does not reload the frame at each key.
    const [openedPage, setOpenedPage] = React.useState<Partial<
        Record<PreviewLanguage, string>
    > | null>(null);
    const latestPolicyPage = React.useRef(policyPage);
    React.useEffect(() => {
        latestPolicyPage.current = policyPage;
    });
    React.useEffect(() => {
        if (policyRequest > 0) {
            setOpenedPage(latestPolicyPage.current);
        }
    }, [policyRequest]);
    // A province the General tab has since taken out falls back on the first.
    const provinceOptions = provinces.length > 0 ? provinces : ['QC'];
    const shownProvince = provinceOptions.includes(province)
        ? province
        : provinceOptions.includes('QC')
        ? 'QC'
        : provinceOptions[0];
    const [preview, setPreview] = React.useState<ContestPreviewToken | null>(
        null
    );
    const [hasFailed, setHasFailed] = React.useState(false);
    const [hasExpired, setHasExpired] = React.useState(false);
    // Bumped to open another preview once one has expired.
    const [opening, setOpening] = React.useState(0);

    React.useEffect(() => {
        let isCurrent = true;
        setPreview(null);
        setHasFailed(false);
        setHasExpired(false);
        store.createPreview(contest.id, start, shownProvince).then((issued) => {
            if (!isCurrent) {
                return;
            }
            if (issued) {
                setPreview(issued);
            } else {
                setHasFailed(true);
            }
        });
        return () => {
            isCurrent = false;
        };
    }, [contest, start, shownProvince, store, opening]);

    React.useEffect(() => {
        if (!preview) {
            return;
        }
        const timer = window.setTimeout(
            () => setHasExpired(true),
            Math.max(0, Date.parse(preview.expiresAt) - Date.now())
        );
        return () => window.clearTimeout(timer);
    }, [preview]);

    // The frame opens in French, or in English for what French lacks. Its own
    // switch changes the language from there.
    const lang: PreviewLanguage =
        openedPage && !openedPage.fr && openedPage.en
            ? 'en'
            : contest.addresses.some((candidate) => candidate.lang === 'fr')
            ? 'fr'
            : 'en';
    const address = contest.addresses.find(
        (candidate) => candidate.lang === lang
    )?.url;
    const frameOrigin = address ? new URL(address).origin : null;

    // Read by the listener, which outlives a render. Set once the render
    // commits, before the effect below posts it.
    const latestTheme = React.useRef(theme);
    const latestPolicies = React.useRef(policies);
    React.useEffect(() => {
        latestTheme.current = theme;
        latestPolicies.current = policies;
    });

    const postTheme = React.useCallback(() => {
        const target = frame.current?.contentWindow;
        if (target && frameOrigin) {
            target.postMessage(
                { type: PREVIEW_THEME_MESSAGE, theme: latestTheme.current },
                frameOrigin
            );
        }
    }, [frameOrigin]);

    const postPolicies = React.useCallback(() => {
        const target = frame.current?.contentWindow;
        if (target && frameOrigin) {
            target.postMessage(
                {
                    type: PREVIEW_POLICIES_MESSAGE,
                    policies: latestPolicies.current,
                },
                frameOrigin
            );
        }
    }, [frameOrigin]);

    React.useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (
                event.source === frame.current?.contentWindow &&
                event.origin === frameOrigin &&
                event.data?.type === PREVIEW_READY_MESSAGE
            ) {
                postTheme();
                postPolicies();
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [frameOrigin, postTheme, postPolicies]);

    // The page builds the theme again on each render: only a change is posted.
    const themeKey = JSON.stringify(theme);
    React.useEffect(postTheme, [themeKey, postTheme]);
    const policiesKey = JSON.stringify(policies);
    React.useEffect(postPolicies, [policiesKey, postPolicies]);

    return (
        <aside className={style.preview} aria-label="Preview">
            <div className={style.controls}>
                <label className={style.setting}>
                    <span>Start as</span>
                    <Select
                        value={start}
                        onChange={(value) => {
                            setStart(value);
                            setOpenedPage(null);
                        }}
                        options={[
                            {
                                label: 'A participant signed in',
                                value: 'participant',
                            },
                            {
                                label: 'A visitor at the sign-up',
                                value: 'visitor',
                            },
                        ]}
                    />
                </label>
                <label className={style.setting}>
                    <span>Province</span>
                    <Select
                        value={shownProvince}
                        onChange={(value) => {
                            setProvince(value);
                            setOpenedPage(null);
                        }}
                        options={provinceOptions.map((name) => ({
                            label: name,
                            value: name,
                        }))}
                    />
                </label>
            </div>
            {/* The phone stays in place whatever it shows, so switching does
                not move the page around it. */}
            <div className={style.phone}>
                <Acvt backgroundColor="white">
                    {address && preview && !hasExpired ? (
                        <iframe
                            ref={frame}
                            // A new token changes only the fragment, which a
                            // frame would not reload for.
                            key={preview.token}
                            className={style.frame}
                            title="Contest preview"
                            // The app opens policies and links in a new tab,
                            // which must leave the sandbox to work.
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                            src={previewUrl(
                                openedPage?.[lang]
                                    ? `${address}/${
                                          POLICY_SEGMENTS[lang]
                                      }/${encodeURIComponent(
                                          openedPage[lang] as string
                                      )}`
                                    : start === 'visitor'
                                    ? `${address}/${SIGNUP_SEGMENTS[lang]}`
                                    : address,
                                preview.token,
                                shownProvince
                            )}
                        />
                    ) : (
                        <div className={style.screen_state}>
                            {!address ? (
                                'This contest has no address in this language.'
                            ) : hasFailed ? (
                                'The preview could not be opened.'
                            ) : hasExpired ? (
                                <>
                                    This preview has expired.
                                    <Button
                                        onClick={() =>
                                            setOpening((count) => count + 1)
                                        }
                                    >
                                        Open it again
                                    </Button>
                                </>
                            ) : (
                                <Spin />
                            )}
                        </div>
                    )}
                </Acvt>
            </div>
        </aside>
    );
};
